/**
 * Nachfrage vor einer Aufgabe, die an nichts haengt (Ticket #22).
 *
 * Eine Aufgabe ohne Kontakt und ohne Vorgang taucht danach weder am einen noch
 * am anderen auf — auffindbar ist sie nur noch ueber die Aufgabenliste. Der
 * Code wusste das laengst und sagte es im eigenen Kommentar
 * („sonst landet die Aufgabe in HERO + /tasks ohne Verknuepfung"), gehindert
 * hat es aber niemanden: gesperrt war der Knopf nur bei leerem Titel.
 *
 * 🔴 Bewusst KEIN Verbot (Entscheidung Paul, 24.08.2026). Es gibt Aufgaben,
 * die zu Recht an nichts haengen. Wer bestaetigt, kommt durch.
 *
 * 🔴 Warum das hier liegt und nicht im Dialog: es gibt VIER Wege, eine Aufgabe
 * anzulegen (Review-Dialog, Anruf-Dialog, Todo-Vorschlag, E-Mail-Fussleiste).
 * Eine Pruefung im Dialog haette genau einen davon abgedeckt — dieselbe Falle
 * wie bei den Katalog-Aktionen, die schon dreimal nur zur Haelfte nachgezogen
 * wurden. Neuer Anlege-Weg? Diese Funktion aufrufen.
 *
 * 🔴 Gefragt wird im Portal-Dialog (`nachfragen`), nicht per `window.confirm`
 * (19.10.2026): hat der Browser Dialoge fuer die Seite abgeschaltet, liefert
 * `confirm()` sofort `false` — der Anlegen-Knopf tat dann stumm nichts. Darum
 * sind alle Pruefungen hier async; Aufrufer `await`en sie.
 */
import { nachfragen } from "@/components/portal/Rueckfrage"

/** Leer ist: null, undefined, 0, "" und "0" — die Pfade liefern gemischt
 *  Zahlen und Strings (`task_prefill` schickt `""`, die Dialoge `null`). */
function leer(wert: number | string | null | undefined): boolean {
  if (wert === null || wert === undefined) return true
  const s = String(wert).trim()
  return s === "" || s === "0"
}

export function hatVerknuepfung(
  contactId: number | string | null | undefined,
  projectId: number | string | null | undefined,
): boolean {
  return !leer(contactId) || !leer(projectId)
}

/**
 * Gibt `true` zurueck, wenn angelegt werden darf.
 *
 * Ist mindestens eines von beiden gesetzt, fragt sie gar nicht erst.
 */
export async function darfOhneVerknuepfungAnlegen(
  contactId: number | string | null | undefined,
  projectId: number | string | null | undefined,
): Promise<boolean> {
  if (hatVerknuepfung(contactId, projectId)) return true
  return nachfragen({
    titel: "Ohne Verknüpfung anlegen?",
    zeilen: [
      "Diese Aufgabe ist weder mit einem Projekt noch mit einem Kontakt verknüpft.",
      "Sie erscheint dann an keinem Vorgang und an keinem Kontakt — auffindbar " +
      "ist sie nur noch über die Aufgabenliste.",
    ],
    ja: "Trotzdem anlegen",
  })
}

/**
 * Hinweis beim Anlegen einer Aufgabe ohne Mitarbeiter oder ohne Projekt
 * (#149, Paul 04.10.2026): „Generell bei Aufgaben einfach einen Hinweis
 * aufpoppen lassen beim Anlegen, wenn kein Monteur oder kein Projekt
 * verknüpft wurde. Es war ein Projekt verknüpft, aber kein
 * Monteur/Mitarbeiter — das ist das Problem."
 *
 * 🔴 Nur warnen, nie sperren: wer bestätigt, legt an. Ersetzt in den
 * Anlege-Wegen `darfOhneVerknuepfungAnlegen` (#22) — die Frage „an nichts
 * verknüpft" steckt hier mit drin, damit es EINE Rückfrage bleibt statt zwei
 * hintereinander (zwei gleich aussehende Popups erziehen zum Wegklicken).
 *
 * `mitarbeiter` ist, was der Dialog als Empfänger schickt (User-ID, "id|name"
 * oder Name). Leer heißt: HERO legt die Aufgabe ohne Empfänger an — sie steht
 * dann bei niemandem.
 */
export type AufgabeZuordnung = {
  mitarbeiter: number | string | null | undefined
  contactId?: number | string | null
  projectId?: number | string | null
  /** Interne Portal-Aufgaben haben keinen Vorgang — dort nur den Mitarbeiter prüfen. */
  ohneProjektPruefung?: boolean
}

export function zuordnungFehlt(args: AufgabeZuordnung): ("mitarbeiter" | "projekt")[] {
  const fehlt: ("mitarbeiter" | "projekt")[] = []
  if (leer(args.mitarbeiter)) fehlt.push("mitarbeiter")
  if (!args.ohneProjektPruefung && leer(args.projectId)) fehlt.push("projekt")
  return fehlt
}

export async function darfAufgabeAnlegen(args: AufgabeZuordnung): Promise<boolean> {
  const fehlt = zuordnungFehlt(args)
  if (fehlt.length === 0) return true
  const zeilen: string[] = []
  if (fehlt.includes("mitarbeiter")) {
    zeilen.push(
      "• Kein Mitarbeiter ausgewählt — die Aufgabe steht dann bei niemandem " +
      "und wird leicht übersehen.",
    )
  }
  if (fehlt.includes("projekt")) {
    zeilen.push(leer(args.contactId)
      ? "• Weder Projekt noch Kontakt verknüpft — auffindbar ist sie nur noch über die Aufgabenliste."
      : "• Kein Projekt verknüpft — am Vorgang sieht die Aufgabe niemand.")
  }
  const titel = fehlt.length === 2
    ? "Ohne Mitarbeiter und ohne Projekt anlegen?"
    : fehlt[0] === "mitarbeiter" ? "Ohne Mitarbeiter anlegen?" : "Ohne Projekt anlegen?"
  return nachfragen({ titel, zeilen, ja: "Trotzdem anlegen" })
}

/**
 * Hinweis beim Buchen eines Termins ohne Mitarbeiter (#148, Paul: „nur
 * warnen"). `automatisch` = der Server sucht selbst einen freien Monteur
 * (Mail-/Anruf-Buchung); dann entsteht der Termin nur ohne Mitarbeiter, wenn
 * keiner frei oder hinterlegt ist. Ohne `automatisch` (Kalender-Plus) bucht
 * der Server genau das, was gewählt ist — also gar keinen.
 */
export async function darfTerminOhneMitarbeiterBuchen(
  anzahlMitarbeiter: number,
  automatisch = false,
): Promise<boolean> {
  if (anzahlMitarbeiter > 0) return true
  const text = automatisch
    ? "Es ist kein Mitarbeiter ausgewählt. Das Portal versucht, einen freien " +
      "Monteur zu nehmen — ist keiner frei oder keiner hinterlegt, steht der " +
      "Termin in HERO OHNE Mitarbeiter."
    : "Es ist kein Mitarbeiter ausgewählt — der Termin steht dann in HERO bei " +
      "niemandem im Kalender."
  return nachfragen({ titel: "Ohne Mitarbeiter buchen?", zeilen: [text], ja: "Trotzdem buchen" })
}
